import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { TabPill } from '../../components';
import { TabPills } from '../../components';
import type { ScheduledEvent } from '../event-details';
import { ActivityTab, useEventActivity } from './activity-tab';
import { CommsTab } from './comms-tab';
import { ContactsTab } from './contacts-tab';
import { DistributionTab } from './distribution-tab';
import { HomeTab } from './home-tab';
import { MarketplaceTab } from './marketplace-tab';
import { NetworkTab } from './network-tab';
import { NotesTab } from './notes-tab';

interface Props {
  scheduledEvent: ScheduledEvent;
}

type TabKey =
  | 'activity'
  | 'comms'
  | 'contacts'
  | 'distribution'
  | 'home'
  | 'marketplace'
  | 'network'
  | 'notes';

interface TabDef {
  key: TabKey;
  label: string;
  /** Only shown for produced events. */
  producedOnly?: boolean;
  hidden?: boolean;
}

// The Activity tab is hidden until every producer is live; set to true to try it (e.g. a staging build).
const SHOW_ACTIVITY_TAB = true;

const TABS: TabDef[] = [
  { key: 'home', label: 'Home' },
  { key: 'activity', label: 'Activity', hidden: !SHOW_ACTIVITY_TAB },
  { key: 'comms', label: 'Comms' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'distribution', label: 'Distribution' },
  { key: 'network', label: 'Network' },
  { key: 'notes', label: 'Notes' },
  { key: 'contacts', label: 'Contacts' }
];

const Card = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: rgba(17, 27, 51, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 13px;
  overflow: hidden;
`;

const Head = styled.div`
  padding: 14px 16px;
`;

// This instance drops the pill-group chrome (border/background/padding).
const BareTabPills = styled(TabPills)`
  border: none;
  background: transparent;
  padding: 0;
`;

const Body = styled.div`
  padding: 20px 18px;
  color: var(--white60);
  font-size: 13px;
`;

export const RightPanel = ({ scheduledEvent }: Props): ReactElement => {
  const [tab, setTab] = useState<TabKey>('home');

  const activity = useEventActivity(Number(scheduledEvent.id), SHOW_ACTIVITY_TAB);

  const tabs = useMemo(
    () => TABS.filter((t) => !t.hidden && (!t.producedOnly || scheduledEvent.event_type === 'Produced')),
    [scheduledEvent.event_type]
  );

  const pills: TabPill[] = tabs.map((t) => ({
    key: t.key,
    label: t.label,
    active: t.key === tab,
    onClick: (): void => {
      setTab(t.key);
    }
  }));

  return (
    <Card>
      <Head>
        <BareTabPills small={false} darker pills={pills} />
      </Head>
      <Body>
        {tab === 'home' ? (
          <HomeTab scheduledEvent={scheduledEvent} />
        ) : tab === 'activity' ? (
          <ActivityTab
            logs={activity.logs}
            loading={activity.loading}
            error={activity.error}
            onRetry={activity.retry}
          />
        ) : tab === 'comms' ? (
          <CommsTab scheduledEvent={scheduledEvent} />
        ) : tab === 'distribution' ? (
          <DistributionTab scheduledEvent={scheduledEvent} />
        ) : tab === 'notes' ? (
          <NotesTab scheduledEvent={scheduledEvent} />
        ) : tab === 'contacts' ? (
          <ContactsTab scheduledEvent={scheduledEvent} />
        ) : tab === 'marketplace' ? (
          <MarketplaceTab />
        ) : (
          <NetworkTab scheduledEvent={scheduledEvent} />
        )}
      </Body>
    </Card>
  );
};
